import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarClock, ThumbsUp, Sparkles, TrendingUp, BarChart3, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";

const FEATURES = [
  {
    icon: MessageSquare,
    title: "Unified inbox",
    body: "Mentions, DMs and comments from Facebook, Instagram, X and LinkedIn land in one queue, triaged by your team, never lost in five different apps.",
  },
  {
    icon: CalendarClock,
    title: "Visual scheduler",
    body: "A drag-and-drop calendar across all four networks with per-network previews, so you see exactly how a post will render before it goes live.",
  },
  {
    icon: ThumbsUp,
    title: "Approval workflows",
    body: "Draft → review → publish, with role-aware approvers and a full audit trail. Nothing reaches your institution's feeds without the right sign-off.",
  },
  {
    icon: Sparkles,
    title: "AI caption & hashtag studio",
    body: "AI-assisted copy and hashtag generation, tuned per platform and language, so your team isn't starting from a blank page every time.",
  },
  {
    icon: TrendingUp,
    title: "Best-time predictions",
    body: "Send-time recommendations driven by your own audience and channel data, not generic industry benchmarks.",
  },
  {
    icon: BarChart3,
    title: "Cross-platform analytics",
    body: "Reach, engagement, sentiment and growth, unified into one dashboard instead of four separate native analytics tabs.",
  },
];

export default function SocialSuitePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-24 overflow-hidden border-b border-border bg-muted/20">
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">Social Suite</span>
              <h1 className="font-display text-4xl md:text-5xl mt-3 text-foreground text-balance">
                A <span className="text-gradient-emerald">full-grade</span> social hub, built into your press operation.
              </h1>
              <p className="text-justify text-muted-foreground mt-5 text-lg leading-relaxed max-w-xl">
                Schedule, approve, publish and measure across Facebook, Instagram, X and LinkedIn
                with the same governance, roles and audit logs as the rest of the platform. Your
                comms team doesn't need a second tool for social, it's already in the orbit.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                {["Facebook", "Instagram", "X / Twitter", "LinkedIn"].map((p) => (
                  <span key={p} className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-border bg-card text-foreground/80">
                    <Share2 className="w-3.5 h-3.5 text-accent" /> {p}
                  </span>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/auth?mode=signup">
                  <Button size="lg" className="gap-2 h-12 px-7">
                    Request institution access <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 gradient-aurora rounded-3xl blur-2xl opacity-50" aria-hidden />
              <div className="relative rounded-2xl border border-border bg-card p-6 shadow-md">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-accent" />
                    <span className="font-display text-sm">This week's queue</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">4 channels · 12 posts</span>
                </div>
                <ul className="space-y-3">
                  {[
                    { ch: "LinkedIn", t: "Wed 09:30", title: "Minister addresses ECOWAS Summit", status: "Approved", color: "text-primary-glow" },
                    { ch: "X", t: "Wed 11:00", title: "Live: press briefing thread", status: "Scheduled", color: "text-accent" },
                    { ch: "Instagram", t: "Thu 14:15", title: "Backstage carousel, 6 frames", status: "In review", color: "text-yellow-500" },
                    { ch: "Facebook", t: "Fri 08:00", title: "Weekly roundup + video", status: "Draft", color: "text-muted-foreground" },
                  ].map((p) => (
                    <li key={p.title} className="flex items-center gap-4 p-3 rounded-xl border border-border bg-background/60">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-20 shrink-0">{p.ch}</span>
                      <span className="text-[11px] font-mono text-muted-foreground w-20 shrink-0">{p.t}</span>
                      <span className="text-sm text-foreground flex-1 truncate">{p.title}</span>
                      <span className={`text-[10px] uppercase tracking-wider ${p.color} shrink-0`}>{p.status}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-border grid grid-cols-3 gap-4 text-center">
                  {[{ k: "Reach", v: "284K" }, { k: "Engagement", v: "6.4%" }, { k: "Sentiment", v: "+82" }].map((m) => (
                    <div key={m.k}>
                      <div className="font-display text-xl text-foreground">{m.v}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.k}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-foreground">Everything your social team needs</h2>
            <p className="text-justify text-muted-foreground mt-3 leading-relaxed">
              Built for institutions that can't afford a misfired post, every feature ships with
              the same role-based access and audit trail as the rest of CONORBIT.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-6 hover-scale">
                <f.icon className="w-5 h-5 text-accent mb-3" />
                <h3 className="font-display text-base text-foreground">{f.title}</h3>
                <p className="text-justify text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
